import React, { useState, useEffect } from 'react';
import { getAdminToken } from '../services/apiService';
import { ADMIN_API_ENDPOINTS } from '../config/api';

const Analytics = () => {
  const [summary, setSummary] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    startDate: '',
    endDate: '',
    page: '',
  });

  // 获取统计汇总
  const fetchSummary = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.ANALYTICS.SUMMARY, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        setError('认证已过期，请重新登录');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setSummary(data.summary);
        setError(null);
      } else {
        const data = await response.json();
        setError(data.message || '获取统计汇总失败');
      }
    } catch (error) {
      console.error('获取统计汇总失败:', error);
      setError('网络错误，请稍后重试');
    }
  };

  // 获取访问记录（每个用户只保留最近一条）
  const fetchVisits = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.startDate) params.append('startDate', filter.startDate);
      if (filter.endDate) params.append('endDate', filter.endDate);
      if (filter.page) params.append('page', filter.page);

      const token = getAdminToken();
      const response = await fetch(
        `${ADMIN_API_ENDPOINTS.ANALYTICS.VISITS}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setError('认证已过期，请重新登录');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const allVisits = data.visits || [];

        // 按用户去重：优先使用user_id，否则使用session_id
        const userMap = {};
        allVisits.forEach((visit) => {
          // 如果有user_id，使用user_id作为key；否则使用session_id
          const userKey = visit.user_id
            ? `user_${visit.user_id}`
            : `session_${visit.session_id || 'unknown'}`;

          if (
            !userMap[userKey] ||
            new Date(visit.created_at) > new Date(userMap[userKey].created_at)
          ) {
            userMap[userKey] = visit;
          }
        });

        // 转换为数组并按时间排序
        const uniqueVisits = Object.values(userMap).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setVisits(uniqueVisits);
        setError(null);
      } else {
        const data = await response.json();
        setError(data.message || '获取访问记录失败');
      }
    } catch (error) {
      console.error('获取访问记录失败:', error);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchVisits();
  }, []);

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0秒';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0) {
      return `${minutes}分${secs}秒`;
    }
    return `${secs}秒`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">访问统计</h1>
        <button
          onClick={() => {
            fetchSummary();
            fetchVisits();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="currentColor"
            viewBox="0 0 256 256"
          >
            <path d="M197.67,186.37a8,8,0,0,1,0,11.29C196.58,198.73,170.82,224,128,224c-37.39,0-64.53-22.4-80-39.85V208a8,8,0,0,1-16,0V160a8,8,0,0,1,8-8H88a8,8,0,0,1,0,16H55.44C67.76,183.35,93,208,128,208c36,0,58.14-21.46,58.36-21.68A8,8,0,0,1,197.67,186.37ZM216,48a8,8,0,0,0-8,8V79.85C192.53,62.4,165.39,40,128,40,85.18,40,59.42,65.27,58.34,66.34a8,8,0,0,0,11.3,11.34C69.86,77.46,92,56,128,56c35,0,60.24,24.65,72.56,40H168a8,8,0,0,0,0,16h48a8,8,0,0,0,8-8V56A8,8,0,0,0,216,48Z"></path>
          </svg>
          <span>刷新</span>
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="mb-6 bg-red-500/20 border border-red-500 text-red-400 px-6 py-4 rounded-lg">
          <div className="flex items-center gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 256 256"
            >
              <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-8-80V80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,172Z"></path>
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* 统计卡片 */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
            <h3 className="text-gray-400 text-sm mb-2">总访问次数</h3>
            <p className="text-3xl font-bold text-blue-400">
              {summary.totalVisits}
            </p>
          </div>
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
            <h3 className="text-gray-400 text-sm mb-2">访问国家数</h3>
            <p className="text-3xl font-bold text-green-400">
              {summary.countryStats.length}
            </p>
          </div>
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
            <h3 className="text-gray-400 text-sm mb-2">页面数量</h3>
            <p className="text-3xl font-bold text-purple-400">
              {summary.pageStats.length}
            </p>
          </div>
        </div>
      )}

      {/* 图表区域 */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* 页面访问排行 */}
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
            <h3 className="text-white text-lg font-bold mb-4">页面访问排行</h3>
            <div className="space-y-3">
              {summary.pageStats.slice(0, 10).map((stat, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-300">{stat.page_name}</span>
                  <span className="text-[#f5a623] font-bold">
                    {stat.visit_count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 国家分布 */}
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
            <h3 className="text-white text-lg font-bold mb-4">
              国家分布 TOP 10
            </h3>
            <div className="space-y-3">
              {summary.countryStats.slice(0, 10).map((stat, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-300">{stat.country}</span>
                  <span className="text-[#4a5f3a] font-bold">
                    {stat.visit_count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 浏览器分布 */}
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
            <h3 className="text-white text-lg font-bold mb-4">浏览器分布</h3>
            <div className="space-y-3">
              {summary.browserStats.map((stat, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-300">{stat.browser}</span>
                  <span className="text-blue-400 font-bold">
                    {stat.visit_count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 设备类型分布 */}
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
            <h3 className="text-white text-lg font-bold mb-4">设备类型分布</h3>
            <div className="space-y-3">
              {summary.deviceStats.map((stat, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-300 capitalize">
                    {stat.device_type}
                  </span>
                  <span className="text-green-400 font-bold">
                    {stat.visit_count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 筛选器 */}
      <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6 mb-6">
        <h3 className="text-white text-lg font-bold mb-4">筛选访问记录</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">开始日期</label>
            <input
              type="date"
              value={filter.startDate}
              onChange={(e) =>
                setFilter({ ...filter, startDate: e.target.value })
              }
              className="w-full px-4 py-2 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">结束日期</label>
            <input
              type="date"
              value={filter.endDate}
              onChange={(e) =>
                setFilter({ ...filter, endDate: e.target.value })
              }
              className="w-full px-4 py-2 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">页面</label>
            <select
              value={filter.page}
              onChange={(e) => setFilter({ ...filter, page: e.target.value })}
              className="w-full px-4 py-2 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
            >
              <option value="">全部</option>
              {summary?.pageStats.map((stat, index) => (
                <option key={index} value={stat.page_name}>
                  {stat.page_name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchVisits}
              className="w-full px-4 py-2 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors"
            >
              查询
            </button>
          </div>
        </div>
      </div>

      {/* 访问记录表格 */}
      <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
        <h3 className="text-white text-lg font-bold p-6 border-b border-[#2a2d27]">
          访问记录 (按用户去重，最近1000条)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#262a22]">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  用户
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  IP地址
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  国家/城市
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  浏览器
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  设备
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  停留时长
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  访问时间
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2d27]">
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    加载中...
                  </td>
                </tr>
              ) : visits.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    暂无数据
                  </td>
                </tr>
              ) : (
                visits.map((visit) => (
                  <tr
                    key={visit.id}
                    className="hover:bg-[#262a22] transition-colors"
                  >
                    <td className="px-4 py-3 text-sm">
                      {visit.username ? (
                        <div className="text-white font-medium">
                          {visit.username}
                        </div>
                      ) : (
                        <div className="text-gray-400">游客</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {visit.ip_address || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {visit.country} / {visit.city}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {visit.browser} {visit.browser_version}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300 capitalize">
                      {visit.device_type}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {formatDuration(visit.visit_duration)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {formatDate(visit.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
